<template>
  <div>
    <h1 class="u-margin-top-0x">
      {{ $t('SignupForm.signUp') }}
    </h1>
    <p>
      {{ $t('SignupForm.alreadyHaveAccount') }}
      <router-link :to="{ name: 'PasswordLogin' }" target="_blank">
        {{ $t('SignupForm.logIn') }}
      </router-link>
    </p>

    <form data-test="signup-user-data" @submit.prevent="formSubmit">
      <div>
        <!-- first name -->
        <BaseFormField
          :label="$t('SignupForm.firstName')"
          label-for="firstName"
          class="u-margin-bottom-4x"
          :error="errorsList.first_name"
        >
          <BaseFormInput
            id="firstName"
            :ref="inputs.first_name"
            v-model="formData.first_name"
            data-test="signup-first-name"
            type="text"
            required
            name="firstName"
          >
          </BaseFormInput>
        </BaseFormField>

        <!-- last name -->
        <BaseFormField
          :label="$t('SignupForm.lastName')"
          label-for="lastName"
          class="u-margin-bottom-4x"
          :error="errorsList.last_name"
        >
          <BaseFormInput
            id="lastName"
            :ref="inputs.last_name"
            v-model="formData.last_name"
            data-test="signup-last-name"
            type="text"
            required
            name="lastName"
          >
          </BaseFormInput>
        </BaseFormField>

        <!-- email -->
        <BaseFormField
          :label="$t('SignupForm.email')"
          label-for="email"
          class="u-margin-bottom-4x"
          :error="errorsList.email"
        >
          <BaseFormInput
            id="email"
            :ref="inputs.email"
            v-model.trim="formData.email"
            data-test="signup-email"
            type="email"
            required
            name="email"
          >
          </BaseFormInput>
        </BaseFormField>

        <!-- password -->
        <PasswordInput
          id="password"
          v-model="formData.password"
          :label="$t('SignupForm.password')"
          :show-errors="showErrors"
          :is-valid.sync="isPasswordValid"
          data-test="signup-password"
          name="password"
          class="u-margin-bottom-4x"
        >
        </PasswordInput>

        <!-- country (conditional) -->
        <BaseFormField
          v-if="!countryId"
          :label="$t('SignupForm.country')"
          label-for="country"
          class="u-margin-bottom-4x"
        >
          <BaseFormSelect
            id="country"
            v-model="formData.country"
            data-cy="signup-country"
            :default-placeholder="$t('SignupForm.yourCountry')"
            :options="countries"
            required
          />
        </BaseFormField>

        <!-- region (conditional) -->
        <BaseFormField
          v-if="showRegion"
          :label="$t(regionsLabel)"
          label-for="region"
          class="u-margin-bottom-4x"
        >
          <BaseFormSelect
            id="region"
            v-model="formData.region"
            :default-placeholder="$t(regionsSelectPlaceholder)"
            option-value="name"
            :options="regions"
            data-test="signup-region"
            required
            name="region"
          >
          </BaseFormSelect>
        </BaseFormField>

        <!-- city (conditional) -->
        <BaseFormField
          v-if="showCity"
          :label="$t('SignupForm.city')"
          label-for="city"
          class="u-margin-bottom-4x"
        >
          <BaseFormSelect
            id="city"
            v-model="formData.city"
            :default-placeholder="$t('SignupForm.selectYourCity')"
            option-value="name"
            :options="cities"
            data-test="signup-city"
            required
            name="city"
          >
          </BaseFormSelect>
        </BaseFormField>

        <!-- postcode (conditional) -->
        <BaseFormField
          v-if="showPostcode"
          :label="$t('SignupForm.postcode')"
          label-for="postcode"
          class="u-margin-bottom-4x"
          :error="errorsList.post_code"
        >
          <BaseFormInput
            id="postcode"
            :ref="inputs.post_code"
            v-model="formData.postcode"
            data-test="signup-postcode"
            type="text"
            required
            name="postcode"
          >
          </BaseFormInput>
        </BaseFormField>

        <!-- fibre availability (conditional) -->
        <BaseFormField
          v-if="showFibreAvailability"
          :label="$t('SignupForm.fibreTitle')"
          label-for="comcomFibre"
          class="u-margin-bottom-4x"
        >
          <template #append>
            <BaseIconTooltip tooltip-icon-name="info-bubble">
              <template #popover-info>
                <p>
                  {{ $t('SignupForm.fibreTooltip') }}
                </p>
              </template>
            </BaseIconTooltip>
          </template>
          <template #description>
            <i18next
              path="SignupForm.fibreDescriptionMap"
              tag="p"
              class="u-margin-bottom-0x u-margin-top-0x"
            >
              <a :href="comComFibreLink" target="_blank">{{
                $t('SignupForm.fibreMap')
              }}</a>
            </i18next>
            <p class="u-margin-bottom-0x u-margin-top-0x">
              {{ $t('SignupForm.fibreDescriptionYes') }}
            </p>
            <p class="u-margin-bottom-0x u-margin-top-0x">
              {{ $t('SignupForm.fibreDescriptionNo') }}
            </p>
          </template>
          <div class="u-flex u-flex-column u-margin-top-2x">
            <BaseFormRadio
              v-model="formData.fibre_availability"
              :option="true"
              name="fibre_availability"
              data-test="signup-fibre-yes"
              required
              >{{ $t('SignupForm.yes') }}</BaseFormRadio
            >
            <BaseFormRadio
              v-model="formData.fibre_availability"
              :option="false"
              name="fibre_availability"
              data-test="signup-fibre-no"
              required
              >{{ $t('SignupForm.no') }}</BaseFormRadio
            >
          </div>
        </BaseFormField>

        <!-- phone number (conditional) -->
        <BaseFormField
          v-if="showPhoneNumber"
          :label="$t('SignupForm.telephoneNumber')"
          label-for="mobile_number"
          class="u-margin-bottom-4x"
          :description="$t('SignupForm.telephoneDescription')"
          :optional="true"
        >
          <BaseFormInput
            id="mobile_number"
            :ref="inputs.mobile_number"
            v-model="formData.mobile_number"
            data-test="signup-number"
            type="tel"
            name="mobile_number"
          ></BaseFormInput>
        </BaseFormField>

        <!-- service provider -->
        <BaseFormField
          :label="$t('SignupForm.serviceProvider')"
          label-for="isp"
          class="u-margin-bottom-4x"
          :has-error="!!ispError"
        >
          <template #append>
            <BaseIconTooltip tooltip-icon-name="info-bubble">
              <template #popover-info>
                <p>
                  {{ $t('SignupForm.providerPackageInfo') }}
                </p>
              </template>
            </BaseIconTooltip>
          </template>
          <template #error>
            <i18next :path="ispError" tag="p" class="u-margin-top-4x">
              <a href="mailto:community@samknows.com" target="_blank">
                {{ $t('utils:common.contactUs') }}
              </a>
            </i18next>
          </template>
          <BaseFormSelect
            id="isp"
            v-model="formData.isp"
            :disabled="ispInputDisabled"
            :default-placeholder="$t('SignupForm.selectYourISP')"
            :options="getIspsForCountry"
            data-test="signup-isp"
            required
            name="isp"
          >
            <template #append-option>
              <option :value="0">
                {{ $t('SignupForm.other') }}
              </option>
              <option :value="-1">
                {{ $t('SignupForm.dunno') }}
              </option>
            </template>
          </BaseFormSelect>
          <BaseFormInput
            v-if="formData.isp === 0"
            :ref="inputs.isp_other"
            v-model="formData.ispOther"
            :placeholder="$t('SignupForm.enterISP')"
            type="text"
            data-test="signup-isp-other"
            class="u-margin-top-4x"
            name="isp-other"
            required
          ></BaseFormInput>
        </BaseFormField>

        <!-- broadband package -->
        <BaseFormField
          :label="$t('SignupForm.internetPackage')"
          label-for="product"
          class="u-margin-bottom-4x"
          :has-error="!!packageError"
        >
          <template #append>
            <BaseIconTooltip tooltip-icon-name="info-bubble">
              <template #popover-info>
                <p>
                  {{ $t('SignupForm.providerPackageInfo') }}
                </p>
              </template>
            </BaseIconTooltip>
          </template>
          <template #error>
            <i18next :path="packageError" tag="p" class="u-margin-top-4x">
              <a href="mailto:community@samknows.com" target="_blank">
                {{ $t('utils:common.contactUs') }}
              </a>
            </i18next>
          </template>
          <BaseFormSelect
            id="product"
            v-model="formData.package"
            :default-placeholder="$t('SignupForm.selectYourPackage')"
            :options="getPackagesForIsp"
            data-test="signup-product"
            required
            name="package"
          >
            <template #append-option>
              <option :value="0">
                {{ $t('SignupForm.other') }}
              </option>
              <option :value="-1">
                {{ $t('SignupForm.dunno') }}
              </option>
            </template>
          </BaseFormSelect>

          <BaseFormInput
            v-if="formData.package === 0"
            :ref="inputs.package_other"
            v-model="formData.packageOther"
            :placeholder="$t('SignupForm.enterPackage')"
            data-test="signup-product-other"
            type="text"
            class="u-margin-top-4x"
            name="package-other"
            required
          ></BaseFormInput>
        </BaseFormField>

        <!-- check for bots -->
        <BaseFormField label="Other" label-for="other" class="u-hidden">
          <BaseFormInput
            id="other"
            :ref="inputs.other"
            v-model="formData.other"
            data-test="signup-other"
            type="text"
            name="other"
          >
          </BaseFormInput>
        </BaseFormField>

        <!-- eula -->
        <BaseFormCheckbox
          v-model="formData.tos"
          data-test="signup-eula"
          required
          name="eula-agreement"
        >
          <i18next path="SignupForm.eula" tag="p" class="u-margin-0x">
            <a :href="eulaLink" target="_blank">{{
              $t('SignupForm.eulaLinkText')
            }}</a>
          </i18next>
        </BaseFormCheckbox>
      </div>

      <BaseAlert
        :value="showGenericError"
        variant="error"
        class="u-margin-top-8x"
        data-cy="signup-generic-error"
        :title="$t('SignupForm.genericError.title')"
      >
        {{ $t('SignupForm.genericError.text') }}
      </BaseAlert>

      <div class="u-margin-top-8x">
        <BaseButton
          :loading="isSubmitting"
          type="submit"
          data-test="signup-submit"
        >
          {{ $t(`utils:common.btnSubmit`) }}
        </BaseButton>
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import Vue, {
  computed,
  defineComponent,
  onBeforeMount,
  PropType,
  reactive,
  Ref,
  ref
} from 'vue';

import {
  createSignup,
  handleErrors,
  scrollToTop
} from '@/features/volunteer-signup/services/signup';
import {
  SignupPayload,
  Package,
  Isp,
  Country,
  FormConfig
} from '@/features/volunteer-signup/types';
import {
  PasswordInput,
  BaseButton,
  BaseFormField,
  BaseFormInput,
  BaseFormCheckbox,
  BaseFormSelect,
  BaseIconTooltip,
  BaseAlert,
  BaseFormRadio
} from '@te/cdev-design-system';
import { formConfig } from '@/features/volunteer-signup/volunteer-form.config';
import { ASYNC_STATE } from '@/common/global.interfaces';
import {
  citiesList,
  createEulaLink,
  ispsForCountry,
  packagesForIsp
} from '@/features/volunteer-signup/helpers/volunteer.helper';
import { heapTrack } from '@/common/services/heapTracking';

export default defineComponent({
  name: 'BasicForm',
  components: {
    BaseButton,
    PasswordInput,
    BaseFormField,
    BaseFormInput,
    BaseFormCheckbox,
    BaseFormSelect,
    BaseIconTooltip,
    BaseAlert,
    BaseFormRadio
  },
  props: {
    ownerId: {
      type: String,
      required: true
    },
    countryId: {
      type: Number,
      default: undefined
    },
    countries: {
      type: Array as PropType<Country[]>,
      required: true
    },
    isp: {
      type: Number,
      default: undefined
    },
    product: {
      type: Number,
      default: undefined
    }
  },
  setup(props, context) {
    const inputs = {
      first_name: ref(null),
      last_name: ref(null),
      email: ref(null),
      mobile_number: ref(null),
      isp_other: ref(null),
      package_other: ref(null),
      post_code: ref(null),
      other: ref(null)
    };
    const isps = ref(undefined);

    const formStatus = ref(ASYNC_STATE.RESOLVED);
    const ispInputDisabled = ref(false);
    const showErrors = ref(false);
    const isPasswordValid = ref(false);
    const errorsList: Ref<Record<string, never>> = ref({});
    const showGenericError = ref(false);

    const formData = reactive({
      owner: props.ownerId,
      first_name: undefined,
      last_name: undefined,
      email: undefined,
      password: undefined,
      country: props.countryId || 235,
      region: undefined,
      city: undefined,
      postcode: undefined,
      isp: undefined,
      package: undefined,
      packageOther: undefined,
      ispOther: undefined,
      // hidden field - "honeypot" for bots to avoid spam signups. FEU PR 223
      other: null,
      tos: false,
      locale: Vue.config.lang?.replace('-', '_') ?? 'en_GB',
      // mobile_number is used as Telephone number
      mobile_number: undefined,
      fibre_availability: undefined
    });

    const formConfigForOwner = computed((): FormConfig => {
      return formConfig[props.ownerId] || {};
    });

    const regionsLabel = computed(() => {
      const regionsLabel = formConfigForOwner.value.regionsLabel;

      return regionsLabel ?? 'SignupForm.region';
    });

    const regionsSelectPlaceholder = computed(() => {
      const regionsSelectPlaceholder =
        formConfigForOwner.value.regionsSelectPlaceholder;
      return regionsSelectPlaceholder ?? 'SignupForm.selectYourRegion';
    });

    const ispError = computed(() => {
      return errorsList.value.isp || errorsList.value.isp_other;
    });

    const packageError = computed(() => {
      return errorsList.value.package || errorsList.value.package_other;
    });

    const getIspsForCountry = computed((): Isp[] => {
      return ispsForCountry(formData.country, props.countries);
    });

    const getPackagesForIsp = computed((): Package[] => {
      return packagesForIsp(formData.isp, getIspsForCountry.value);
    });

    const regions = computed(() => {
      const language = formData.locale.split('_')[0];
      const listOfRegions = formConfigForOwner.value.regions?.[language];

      return listOfRegions?.map((region: string) => {
        return {
          name: region
        };
      });
    });

    const showRegion = computed(() => {
      return !!regions.value;
    });
    const cities = computed(() => {
      const language = formData.locale.split('_')[0];
      return citiesList(language, props.ownerId);
    });
    const showCity = computed(() => {
      return !!cities.value;
    });
    const showPostcode = computed(() => {
      return formConfigForOwner.value.showPostcodeField;
    });
    const showFibreAvailability = computed(() => {
      return formConfigForOwner.value.showFibreAvailability;
    });
    const comComFibreLink =
      'https://comcom.govt.nz/regulated-industries/telecommunications/regulated-services/consumer-protections-for-copper-withdrawal/map-of-specified-fibre-areas';
    const showPhoneNumber = computed(() => {
      return formConfigForOwner.value.showPhoneNumberField;
    });

    const eulaLink = computed((): string => {
      return createEulaLink(props.ownerId);
    });
    const isSubmitting = computed(() => {
      return formStatus.value === ASYNC_STATE.LOADING;
    });

    async function formSubmit() {
      // hidden field - "honeypot" for bots to avoid spam signups. FEU PR 223
      if (formData.other !== null) {
        return;
      }

      if (!isPasswordValid.value) {
        showErrors.value = true;
        return;
      }

      formStatus.value = ASYNC_STATE.LOADING;
      showGenericError.value = false;
      errorsList.value = {};

      if (formData.isp !== 0) {
        formData.ispOther = '';
      }

      if (formData.package !== 0) {
        formData.packageOther = '';
      }

      const payload: SignupPayload = {
        ...formData
      };

      if (payload.isp === -1) {
        payload.isp = null;
      }

      if (payload.package === -1) {
        payload.package = null;
      }

      try {
        await createSignup(payload);
        formStatus.value = ASYNC_STATE.RESOLVED;
        scrollToTop();
        context.emit('success');
        heapTrack('SignupSubmitSuccess');
      } catch (error) {
        if (error instanceof Response) {
          formStatus.value = ASYNC_STATE.ERROR;
          const signupErrors = await handleErrors(
            error,
            inputs,
            errorsList.value
          );
          showGenericError.value = signupErrors.showGenericError;
        } else {
          heapTrack('SignupSubmitError', {
            type: 'uncaught'
          });
          showGenericError.value = true;
          console.error(error);
        }
      }
    }

    onBeforeMount(() => {
      if (props.isp) {
        formData.isp = props.isp;
        ispInputDisabled.value = true;
      }
      if (props.product) {
        formData.package = props.product;
      }
    });

    return {
      inputs,
      isps,
      formStatus,
      ispInputDisabled,
      showErrors,
      isPasswordValid,
      errorsList,
      formData,
      eulaLink,
      regionsLabel,
      regionsSelectPlaceholder,
      ispError,
      packageError,
      getIspsForCountry,
      getPackagesForIsp,
      regions,
      showRegion,
      cities,
      showCity,
      showPostcode,
      showFibreAvailability,
      comComFibreLink,
      showPhoneNumber,
      isSubmitting,
      showGenericError,
      formSubmit
    };
  }
});
</script>
